import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Radio, Spin, Slider, Switch } from 'antd';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

// Fix for default markers in Leaflet with webpack
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

/**
 * Anything from the database that lands inside popup HTML goes through here.
 *
 * Leaflet's bindPopup() takes a STRING and renders it as markup, so every
 * template literal below is an injection point. `session_name` is the reason
 * this exists now: it is sar.name, which SafetyJourney builds from the linked
 * user's own profile name — a value an ordinary user can set, arriving in a
 * popup somebody else opens. Zone and device names are staff-set and were
 * already interpolated raw; they get the same treatment because the cost is
 * nothing and the alternative is remembering which of these strings is safe.
 */
const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const ActivityMap = ({ 
  breadcrumbData, 
  loading, 
  onSliderChange, 
  sliderRange, 
  filteredBreadcrumbData, // This prop might not exist when called from Activity component
  isEmbedded = false, // New prop to distinguish usage context
  // May these points be joined into a path? A tracker's fixes may — they are
  // samples of a continuous journey. Points a worker CHOSE to share may not:
  // nothing is known between them, and a line would draw a route nobody
  // claimed to have taken. Defaults true so every existing device is unchanged.
  connectPoints = true,
  // Sessions represented in the current data, oldest first. Each gets its own
  // colour so points from separate trips are never read as one journey.
  sessions = []
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const heatmapLayerRef = useRef(null);
  const [mapMode, setMapMode] = useState('markers');
  /**
   * Join a session's shared points with a dashed line.
   *
   * OFF by default, and the default is the honest one: nothing is known
   * between two shared points, so any line is inferred rather than travelled.
   * It is offered because a faint line can make drift within one session
   * readable at a glance — but it is never drawn ACROSS sessions, and never as
   * a solid line that would pass for a recorded track.
   */
  /**
   * Which sessions have their points joined by a dashed line, by session key.
   *
   * PER SESSION, not one switch for the map. A day's view can hold several
   * trips at once, and the question "did this one wander?" is asked about one
   * of them at a time — a single toggle drew every line on the map at once and
   * buried the trip being looked at under the others.
   *
   * Empty by default, and the default is the honest one: nothing is known
   * between two shared points, so any line is inferred rather than travelled.
   */
  const [linkedSessionIds, setLinkedSessionIds] = useState([]);

  /**
   * Which session a point or legend row belongs to.
   *
   * A session is now either a safety trip or a contractor visit, and the two
   * are numbered from different tables — so the key is a prefixed string from
   * the API rather than a bare id. The fallback keeps an older API response
   * (safety points only, no session_key) working unchanged.
   */
  const sessionKeyOf = useCallback(
    (item) => item?.session_key || (item?.sar_id ? `sar:${item.sar_id}` : null),
    []
  );

  const isSessionLinked = useCallback(
    (key) => linkedSessionIds.includes(key),
    [linkedSessionIds]
  );

  const toggleSessionLink = useCallback((key, on) => {
    setLinkedSessionIds((current) =>
      on ? [...new Set([...current, key])] : current.filter((id) => id !== key)
    );
  }, []);
  
  // Internal state for filtered data - use this when not provided externally
  const [internalFilteredData, setInternalFilteredData] = useState([]);
  const [internalSliderRange, setInternalSliderRange] = useState([0, 0]);

  // Determine which data and range to use
  const currentFilteredData = filteredBreadcrumbData || internalFilteredData;
  const currentSliderRange = sliderRange || internalSliderRange;

  // Function to calculate bearing between two points
  const calculateBearing = (lat1, lng1, lat2, lng2) => {
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    
    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
    
    let bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360;
  };

  // Handle map mode change
  const handleMapModeChange = (e) => {
    setMapMode(e.target.value);
  };

  // Internal slider change handler
  const handleInternalSliderChange = useCallback((range) => {
    setInternalSliderRange(range);
    
    if (breadcrumbData && breadcrumbData.length > 0) {
      const startIndex = range[0];
      const endIndex = range[1] + 1; // +1 because slice end is exclusive
      const filtered = breadcrumbData.slice(startIndex, endIndex);
      setInternalFilteredData(filtered);
    }
    
    // Call external handler if provided
    if (onSliderChange) {
      onSliderChange(range);
    }
  }, [breadcrumbData, onSliderChange]);

  // Update internal state when breadcrumbData changes
  useEffect(() => {
    if (breadcrumbData && breadcrumbData.length > 0) {
      const maxIndex = Math.max(0, breadcrumbData.length - 1);
      
      // Only update internal state if not using external filtered data
      if (!filteredBreadcrumbData) {
        setInternalSliderRange([0, maxIndex]);
        setInternalFilteredData(breadcrumbData);
      }
    } else {
      setInternalFilteredData([]);
      setInternalSliderRange([0, 0]);
    }
  }, [breadcrumbData, filteredBreadcrumbData]);

  // Function to clear existing markers and polylines
  const clearMapLayers = useCallback(() => {
    if (mapInstanceRef.current) {
      const map = mapInstanceRef.current;
      
      // Remove all markers and polylines
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.Polyline) {
          map.removeLayer(layer);
        }
      });
      
      // Remove heatmap layer if it exists
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }
    }
  }, []);

// Function to render markers mode
// One colour per tracking session. Points from two different trips look
// identical otherwise — a worker who went out at 9am and again at 3pm would
// have both sets read as one.
const SESSION_COLOURS = ['#1677ff', '#fa8c16', '#52c41a', '#eb2f96', '#722ed1', '#13c2c2'];

const sessionColour = useCallback((key) => {
  if (!key) return '#8c8c8c'; // shared outside any session
  const index = sessions.findIndex((s) => sessionKeyOf(s) === key);
  return SESSION_COLOURS[(index < 0 ? 0 : index) % SESSION_COLOURS.length];
}, [sessions, sessionKeyOf]);

const renderSharedPointsMode = useCallback(() => {
  if (!mapInstanceRef.current || currentFilteredData.length === 0) return;

  const map = mapInstanceRef.current;
  const points = currentFilteredData;

  // Every point is its own pin, all the same weight WITHIN a session. No line,
  // no start/end colouring, no ordering implied — the worker shared these, and
  // the gaps between them mean "they did not share", never "they did not
  // move". Colour groups them by session; it does not sequence them.
  // Optional dashed links, drawn UNDER the pins and only within a session.
  {
    const bySession = points.reduce((groups, point) => {
      const key = sessionKeyOf(point) || 'none';
      (groups[key] = groups[key] || []).push(point);
      return groups;
    }, {});

    Object.entries(bySession).forEach(([key, sessionPoints]) => {
      if (sessionPoints.length < 2) return;

      // Points outside any session are never joined: there is no trip to say
      // they belong to the same journey.
      if (key === 'none' || !isSessionLinked(key)) return;

      const colour = sessionColour(key);
      const latLngs = sessionPoints.map(p => [p.lat, p.lng]);

      // A CASING FIRST. A thin coloured dash disappears into satellite
      // imagery — roofs, roads and shadows supply every colour it might use.
      // A solid dark line underneath gives the dashes something constant to
      // sit against, which is how map labels stay readable over aerial
      // photography. It carries no meaning of its own.
      L.polyline(latLngs, {
        color: '#1f1f1f',
        weight: 6,
        opacity: 0.55,
        lineJoin: 'round',
        interactive: false
      }).addTo(map);

      // An arrowhead per segment, pointing from each shared point to the next.
      // The dashes say "we do not know the route"; the arrow says "this one
      // came first", which is a fact — the order they arrived. Without it a
      // line reads equally well backwards, and the first question anybody asks
      // of a map like this is which way the worker was going.
      latLngs.forEach((from, i) => {
        if (i === latLngs.length - 1) return;

        const to = latLngs[i + 1];
        const bearing = calculateBearing(from[0], from[1], to[0], to[1]);
        const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];

        L.marker(mid, {
          interactive: false,
          icon: L.divIcon({
            className: '',
            iconSize: [14, 14],
            iconAnchor: [7, 7],
            // Rotated so 0deg points north, matching the bearing.
            html: `<div style="transform: rotate(${bearing}deg); width: 14px; height: 14px;">`
              + `<div style="width: 0; height: 0; margin: 0 auto;`
              + ` border-left: 5px solid transparent; border-right: 5px solid transparent;`
              + ` border-bottom: 10px solid ${colour};`
              + ` filter: drop-shadow(0 0 1.5px rgba(0,0,0,0.85));"></div></div>`
          })
        }).addTo(map);
      });

      L.polyline(latLngs, {
        color: colour,
        // Opaque and thick enough to read at a glance. It stays DASHED, which
        // is what says "inferred" — legibility and honesty are different
        // problems, and dimming the line solved neither.
        weight: 3.5,
        opacity: 1,
        dashArray: '10, 8',
        lineCap: 'butt',
        lineJoin: 'round'
      })
        .addTo(map)
        .bindPopup(
          '<div style="min-width: 200px;">'
          + '<b>Inferred link</b><br>'
          + '<div style="color: #666; font-size: 12px; margin-top: 4px;">'
          + 'Joins points shared during one tracking session, in the order they arrived.'
          + '</div>'
          + '<div style="color: #999; font-size: 11px; margin-top: 6px;">'
          + 'NOT a travelled route. Nothing is known about where the worker was between them.'
          + '</div></div>'
        );
    });
  }

  // Order WITHIN each session, so the numbers restart per trip rather than
  // running across unrelated ones. currentFilteredData is already oldest-first.
  const orderInSession = {};
  const sessionTotals = {};
  points.forEach((point) => {
    const key = sessionKeyOf(point) || 'none';
    sessionTotals[key] = (sessionTotals[key] || 0) + 1;
  });

  points.forEach((point) => {
    const pointKey = sessionKeyOf(point);
    const colour = sessionColour(pointKey);
    const key = pointKey || 'none';
    orderInSession[key] = (orderInSession[key] || 0) + 1;
    const position = orderInSession[key];
    const total = sessionTotals[key];

    // Numbered ONLY while this session's line is drawn.
    //
    // Sequence and route are answers to one question — "which way did this
    // trip go?" — so they appear together or not at all. Numbering every pin
    // all the time turns a map of three sessions into forty numerals competing
    // for attention, most of them about a trip nobody is looking at.
    const numbered = isSessionLinked(pointKey);

    const markerIcon = numbered
      ? L.divIcon({
          html: `<div style="background-color: ${colour}; width: 22px; height: 22px; border-radius: 50%;`
            + ` border: 2px solid #ffffff; box-shadow: 0 2px 5px rgba(0,0,0,0.45); color: #ffffff;`
            + ` font: 700 11px/22px -apple-system, system-ui, sans-serif; text-align: center;">${position}</div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
          className: ''
        })
      : L.divIcon({
          html: `<div style="background-color: ${colour}; width: 18px; height: 18px; border-radius: 50%;`
            + ` border: 3px solid #ffffff; box-shadow: 0 2px 5px rgba(0,0,0,0.4);"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          className: ''
        });

    const sessionLine = point.session_name
      ? `<div style="color: ${colour}; font-weight: 600; font-size: 12px; margin-bottom: 2px;">
           ${escapeHtml(point.session_name)}${point.session_started ? ` · started ${escapeHtml(point.session_started)}` : ''}
         </div>`
      : '';

    const checkLine = point.check_due
      ? `<div style="color: #666; font-size: 11px;">Safety check due: ${escapeHtml(point.check_due)}</div>`
      : '';

    const popupContent = `
      <div style="min-width: 220px;">
        ${sessionLine}
        <b>📍 Shared point${numbered ? ` ${position} of ${total}` : ''}</b><br>
        <div style="color: ${colour}; font-weight: 600; margin: 4px 0;">
          ${escapeHtml(point.time)}
        </div>
        <div style="color: #666; font-size: 12px;">
          ${escapeHtml(point.name || 'Shared by the worker')}
        </div>
        ${checkLine}
        <div style="color: #999; font-size: 11px; margin-top: 4px;">
          Lat: ${point.lat.toFixed(6)}<br>
          Lng: ${point.lng.toFixed(6)}
        </div>
        <div style="color: #999; font-size: 11px; margin-top: 6px; border-top: 1px solid #eee; padding-top: 4px;">
          Sent by the worker — not tracked. Their position between points is unknown.
        </div>
      </div>
    `;

    L.marker([point.lat, point.lng], { icon: markerIcon }).addTo(map).bindPopup(popupContent);
  });

  if (points.length === 1) {
    map.setView([points[0].lat, points[0].lng], 15);
    return;
  }

  map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [40, 40] });
}, [currentFilteredData, sessionColour, isSessionLinked, sessionKeyOf]);

const renderMarkersMode = useCallback(() => {
  if (!mapInstanceRef.current || currentFilteredData.length === 0) return;

    const map = mapInstanceRef.current;
    const activityPoints = currentFilteredData;
    const trailCoordinates = activityPoints.map(point => [point.lat, point.lng]);

    // Draw polyline segments with gradient colors - vibrant colors to stand out
    trailCoordinates.forEach((coord, index) => {
      if (index < trailCoordinates.length - 1) {
        const nextCoord = trailCoordinates[index + 1];
        const progress = index / (trailCoordinates.length - 1);
        
        // Bright, contrasting colors that stand out against Australian terrain
        const red = Math.round(255 - (progress * 140));   // 255 -> 115 (bright red to purple-red)
        const green = Math.round(50 + (progress * 150));  // 50 -> 200 (dark to bright green)
        const blue = Math.round(100 + (progress * 155));  // 100 -> 255 (purple to cyan-blue)
        
        const segmentColor = `rgb(${red}, ${green}, ${blue})`;
        
        // showMeasurements: false, because the trail is drawn one two-point
        // polyline per hop — and mapMeasurements.js turns the labels on for
        // every L.Polyline in the app, which here means a number on every
        // breadcrumb-to-breadcrumb leg, twice over (the plugin adds a total
        // length to each one alongside the segment length). This map is read
        // for where something went, so it stays a clean line as it was before
        // the measurement plugin arrived. The property's "Distance and area on
        // shapes" setting is about labelling drawn paddocks and has no say here
        // either way.
        L.polyline([coord, nextCoord], {
          showMeasurements: false,
          color: segmentColor,
          weight: 5,
          opacity: 1.0,
          lineJoin: 'round'
        }).addTo(map);
      }
    });

    // Add markers only for start and end points
    activityPoints.forEach((point, index) => {
      // Only show markers for start (index 0) and end (last index) points
      if (index === 0 || index === activityPoints.length - 1) {
        let markerIcon;
        let popupContent;
        
        if (index === 0) {
          // Start marker - Australian gold/ochre color
          markerIcon = L.divIcon({
            html: '<div style="background-color: #cc7722; width: 16px; height: 16px; border-radius: 50%; border: 2px solid #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.4);"></div>',
            iconSize: [16, 16],
            iconAnchor: [8, 8],
            className: ''
          });
          
          popupContent = `
            <div style="min-width: 180px;">
              <b>🚀 Journey Start</b><br>
              <div style="color: #cc7722; font-weight: 600; margin: 4px 0;">
                ${escapeHtml(point.time)}
              </div>
              <div style="color: #666; font-size: 12px;">
                ${escapeHtml(point.name || 'Starting Location')}
              </div>
              <div style="color: #999; font-size: 11px; margin-top: 4px;">
                Lat: ${point.lat.toFixed(6)}<br>
                Lng: ${point.lng.toFixed(6)}
              </div>
            </div>
          `;
        } else if (index === activityPoints.length - 1) {
          // End marker - Australian eucalyptus green
          markerIcon = L.divIcon({
            html: '<div style="background-color: #6b8e23; width: 16px; height: 16px; border-radius: 50%; border: 2px solid #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.4);"></div>',
            iconSize: [16, 16],
            iconAnchor: [8, 8],
            className: ''
          });
          
          popupContent = `
            <div style="min-width: 180px;">
              <b>🏁 Journey End</b><br>
              <div style="color: #6b8e23; font-weight: 600; margin: 4px 0;">
                ${escapeHtml(point.time)}
              </div>
              <div style="color: #666; font-size: 12px;">
                ${escapeHtml(point.name || 'Ending Location')}
              </div>
              <div style="color: #999; font-size: 11px; margin-top: 4px;">
                Lat: ${point.lat.toFixed(6)}<br>
                Lng: ${point.lng.toFixed(6)}
              </div>
            </div>
          `;
        }

        const marker = L.marker([point.lat, point.lng], { icon: markerIcon }).addTo(map);
        marker.bindPopup(popupContent);
      }
    });

    if (trailCoordinates.length > 0) {
      const bounds = L.latLngBounds(trailCoordinates);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [currentFilteredData]);

  // Function to render heatmap mode
  const renderHeatmapMode = useCallback(() => {
    if (!mapInstanceRef.current || currentFilteredData.length === 0) return;

    const map = mapInstanceRef.current;
    
    // Prepare heatmap data - each point is [lat, lng, intensity]
    const heatmapData = currentFilteredData.map((point, index) => {
      // Calculate intensity based on time spent at location or frequency
      // For this example, we'll use a base intensity and add some variation
      const baseIntensity = 0.5;
      const timeVariation = Math.random() * 0.5; // Random variation for demo
      const intensity = Math.min(1.0, baseIntensity + timeVariation);
      
      return [point.lat, point.lng, intensity];
    });

    // Create heatmap layer
    const heatmapOptions = {
      radius: 25,
      blur: 15,
      maxZoom: 17,
      max: 1.0,
      gradient: {
        0.0: '#000080',  // Navy blue for low intensity
        0.2: '#0000FF',  // Blue
        0.4: '#00FFFF',  // Cyan
        0.6: '#00FF00',  // Green
        0.8: '#FFFF00',  // Yellow
        1.0: '#FF0000'   // Red for high intensity
      }
    };

    heatmapLayerRef.current = L.heatLayer(heatmapData, heatmapOptions).addTo(map);

    // Fit bounds to show all data
    if (currentFilteredData.length > 0) {
      const coordinates = currentFilteredData.map(point => [point.lat, point.lng]);
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [currentFilteredData]);

  // Initialize map only once
  useEffect(() => {
    if (!mapInstanceRef.current && mapRef.current) {
      const map = L.map(mapRef.current, {
        zoomControl: false
      }).setView([-37.8136, 144.9631], 13);

      const jawgAttribute = {
        attribution: '<a href="http://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        minZoom: 0,
        maxZoom: 22,
        accessToken: process.env.REACT_APP_JAWG_TOKEN
      };

      var jawgLight = L.tileLayer(`https://{s}.tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token=${process.env.REACT_APP_JAWG_TOKEN}`, jawgAttribute);
      var jawgDark = L.tileLayer(`https://{s}.tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=${process.env.REACT_APP_JAWG_TOKEN}`, jawgAttribute);
      var jawgTerrain = L.tileLayer(`https://{s}.tile.jawg.io/jawg-terrain/{z}/{x}/{y}{r}.png?access-token=${process.env.REACT_APP_JAWG_TOKEN}`, jawgAttribute);
      var esriWorldImagery = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      });

      const baseLayers = {
        "Jawg Light": jawgLight,
        "Jawg Dark": jawgDark,
        "Jawg Terrain": jawgTerrain,
        "Esri Satellite": esriWorldImagery,
      };

      esriWorldImagery.addTo(map);
      L.control.layers(baseLayers, null, { position: 'topright' }).addTo(map);
      L.control.zoom({ position: 'topleft' }).addTo(map);
      mapInstanceRef.current = map;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update map when filtered data or map mode changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Clear existing layers
      clearMapLayers();

      if (currentFilteredData.length === 0) return;

      // Render based on selected mode. Points that may not be joined get their
      // own renderer rather than a flag inside the trail one — the trail draws
      // polylines, start/end markers and a direction gradient, none of which
      // this data can support.
      if (mapMode === 'markers') {
        if (connectPoints) {
          renderMarkersMode();
        } else {
          renderSharedPointsMode();
        }
      } else if (mapMode === 'heatmap') {
        renderHeatmapMode();
      }
    }
  }, [currentFilteredData, mapMode, connectPoints, linkedSessionIds, clearMapLayers,
      renderMarkersMode, renderSharedPointsMode, renderHeatmapMode]);

  // Calculate max slider value
  const maxSliderValue = Math.max(0, (breadcrumbData?.length || 1) - 1);

  return (
    <div className="map-container">
      <div ref={mapRef} className="leaflet-map" />
      {loading && (
        <div className="loading-overlay">
          <Spin size="small" />
          Loading location data...
        </div>
      )}
      
      {/* Map Mode Toggle - Updated z-index */}
      <div className="map-mode-toggle" style={{ 
        position: 'absolute', 
        bottom: 10, 
        left: 10, 
        background: '#fff', 
        padding: 10, 
        borderRadius: 6, 
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)', 
        zIndex: 1000 // Changed from 1000 to 100
      }}>
        <Radio.Group value={mapMode} onChange={handleMapModeChange} size="small">
          <Radio.Button value="markers">{connectPoints ? 'Markers' : 'Shared points'}</Radio.Button>
          <Radio.Button value="heatmap">Heatmap</Radio.Button>
        </Radio.Group>
      </div>
      
      {/* Breadcrumb Range Slider Overlay */}
      {!connectPoints && sessions.length > 0 && (
        <div
          style={{
            // Clears the map-mode toggle, which is anchored at bottom: 10 and
            // stands ~52px tall (10px padding + a 32px control). Sitting the
            // legend directly on top of it hid whichever was rendered second.
            // Left edges aligned so the two read as one stack.
            position: 'absolute', bottom: 74, left: 10, zIndex: 1000,
            background: 'rgba(255,255,255,0.94)', borderRadius: 6, padding: '8px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)', maxWidth: 260, fontSize: 12
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 10, marginBottom: 6 }}>
            <span style={{ fontWeight: 600 }}>Tracking sessions</span>
            <span style={{ color: '#999', fontSize: 11 }}>join</span>
          </div>

          {sessions.map((session, index) => (
            <div
              key={sessionKeyOf(session)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}
            >
              <span
                style={{
                  width: 10, height: 10, borderRadius: '50%', flex: '0 0 auto',
                  background: SESSION_COLOURS[index % SESSION_COLOURS.length]
                }}
              />
              {/* The NAME first, now that a session may be a lone worker's trip
                  or a contractor's visit. A column of start times alone gives
                  no way to tell whose line is whose. */}
              <span style={{ color: '#555', flex: '1 1 auto', minWidth: 0 }}>
                {session.name && (
                  <span
                    style={{ display: 'block', fontWeight: 500, overflow: 'hidden',
                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={session.name}
                  >
                    {session.name}
                  </span>
                )}
                {session.started}
                {session.interval_minutes ? ` · every ${session.interval_minutes} min` : ''}
                {session.is_open ? ' · open' : ''}
              </span>
              {/* One switch per session: the question "did this trip wander?"
                  is asked about one trip at a time. */}
              <Switch
                size="small"
                checked={isSessionLinked(sessionKeyOf(session))}
                onChange={(on) => toggleSessionLink(sessionKeyOf(session), on)}
              />
            </div>
          ))}

          {sessions.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                          borderTop: '1px solid #eee', paddingTop: 6 }}>
              <Switch
                size="small"
                checked={linkedSessionIds.length === sessions.length}
                onChange={(on) =>
                  setLinkedSessionIds(on ? sessions.map((session) => sessionKeyOf(session)) : [])
                }
              />
              <span style={{ color: '#555' }}>All sessions</span>
            </div>
          )}

          <div style={{ color: '#999', marginTop: 6 }}>
            {linkedSessionIds.length > 0
              ? 'Numbers and arrows show the order points were SHARED. The dashed line is '
                + 'inferred — nothing is known about where they were between them.'
              : 'Points the worker chose to send. Switch a session on to see the order they arrived.'}
          </div>
        </div>
      )}

      {breadcrumbData && breadcrumbData.length > 0 && (
        <div className="slider-overlay">
          {currentFilteredData.length > 0 && (
            <div className="slider-time-range">
              {currentFilteredData[0]?.time} - {currentFilteredData[currentFilteredData.length - 1]?.time}
            </div>
          )}
          
          <div style={{ marginBottom: '6px' }}>
            <Slider
              range
              min={0}
              max={maxSliderValue}
              value={currentSliderRange}
              onChange={filteredBreadcrumbData ? onSliderChange : handleInternalSliderChange}
              tooltip={{ open: false }}
              step={1}
            />
          </div>
          
          <div className="slider-time-labels">
            <span>{breadcrumbData[0]?.time}</span>
            <span>{breadcrumbData[breadcrumbData.length - 1]?.time}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityMap;